import { adminRoleAccess, restrictedRolesAccess } from '@/services/roles/roles.js';

const excludedUserFilterRoleKeys = new Set(restrictedRolesAccess);

/** Роли компании из API — нужен id. */
export const isRoleAllowedInUserFilter = (role) =>
  role?.id != null && !excludedUserFilterRoleKeys.has(role.key);

/** Запись roles из /properties — id часто нет, достаточно key. */
const isCatalogRoleForDefaultFilter = (role) =>
  role?.key &&
  isStandardUserFilterRoleKey(role.key) &&
  !excludedUserFilterRoleKeys.has(role.key);

const standardRoleKeyOrder = new Map(
  adminRoleAccess.map((key, index) => [key, index])
);

export const isStandardUserFilterRoleKey = (key) =>
  standardRoleKeyOrder.has(key);

const mapCompanyRoleToOption = (role) => ({
  id: role.id,
  key: role.key,
  label: role.value || role.key
});

const mapCatalogRoleToOption = (role, key, getRoleLabel) => ({
  id: role?.id ?? key,
  key: role?.key ?? key,
  label: role?.value || role?.key || getRoleLabel?.(key) || key
});

/** Условия для POST /admin/users/query (как role_key в create.vue). */
export const buildRoleFilterConditions = (selectedRoleIds, roleOptions = []) => {
  if (!Array.isArray(selectedRoleIds) || !selectedRoleIds.length) return [];

  const optionById = new Map(
    roleOptions.map((option) => [String(option.id), option])
  );

  const roleKeys = new Set();
  const roleIds = [];

  for (const selected of selectedRoleIds) {
    const selectedStr = String(selected);
    const option = optionById.get(selectedStr);

    if (option?.key && isStandardUserFilterRoleKey(option.key)) {
      roleKeys.add(option.key);
      continue;
    }

    if (option?.key) {
      roleIds.push(String(option.id));
      continue;
    }

    if (isStandardUserFilterRoleKey(selectedStr)) {
      roleKeys.add(selectedStr);
      continue;
    }

    roleIds.push(selectedStr);
  }

  const conditions = [];
  if (roleKeys.size) {
    conditions.push({ field: 'role_key', values: [...roleKeys] });
  }
  if (roleIds.length) {
    conditions.push({ field: 'role_id', values: roleIds });
  }
  return conditions;
};

const compareRolesForFilter = (a, b) => {
  const aOrder = standardRoleKeyOrder.has(a.key)
    ? standardRoleKeyOrder.get(a.key)
    : Number.MAX_SAFE_INTEGER;
  const bOrder = standardRoleKeyOrder.has(b.key)
    ? standardRoleKeyOrder.get(b.key)
    : Number.MAX_SAFE_INTEGER;
  if (aOrder !== bOrder) return aOrder - bOrder;
  const aLabel = String(a.value ?? a.key ?? '');
  const bLabel = String(b.value ?? b.key ?? '');
  return aLabel.localeCompare(bLabel, 'ru', { sensitivity: 'base' });
};

/** Все роли из ответа API (стандартные + кастомные компании). */
export const mapApiRolesToFilterOptions = (roles) => {
  if (!Array.isArray(roles)) return [];

  return roles
    .filter(isRoleAllowedInUserFilter)
    .sort(compareRolesForFilter)
    .map(mapCompanyRoleToOption);
};

/** Без выбранной компании: NO_ACCESS, OBSERVER, READER, OPERATOR, EDITOR, ADMIN. */
export const defaultUserFilterRoleKeys = adminRoleAccess;

/** Справочник properties → опции фильтра (только defaultUserFilterRoleKeys, фиксированный порядок). */
export const mapStandardRoleFilterOptions = (catalogRoles, getRoleLabel) => {
  const byKey = new Map();

  if (Array.isArray(catalogRoles)) {
    for (const role of catalogRoles) {
      if (!isCatalogRoleForDefaultFilter(role)) continue;
      byKey.set(role.key, role);
    }
  }

  return defaultUserFilterRoleKeys.map((key) =>
    mapCatalogRoleToOption(byKey.get(key), key, getRoleLabel)
  );
};

export const mapRolesToFilterOptions = (roles) => {
  if (!Array.isArray(roles)) return [];

  return roles
    .filter((role) => isStandardUserFilterRoleKey(role.key) && isRoleAllowedInUserFilter(role))
    .sort(
      (a, b) =>
        (standardRoleKeyOrder.get(a.key) ?? 0) -
        (standardRoleKeyOrder.get(b.key) ?? 0)
    )
    .map(mapCompanyRoleToOption);
};

export const normalizeCompanyRolesResponse = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.roles)) return raw.roles;
  if (raw && Array.isArray(raw.list)) return raw.list;
  if (raw && Array.isArray(raw.data)) return raw.data;
  return [];
};

/** Объединение ролей нескольких компаний — без отсечения кастомных ролей. */
export const mergeCompanyRoles = (roleGroups) => {
  const byId = new Map();
  for (const group of roleGroups) {
    if (!Array.isArray(group)) continue;
    for (const role of group) {
      if (!isRoleAllowedInUserFilter(role)) continue;
      byId.set(String(role.id), role);
    }
  }
  return mapApiRolesToFilterOptions([...byId.values()]);
};
